import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { formatPhoneNumber, formatPhoneInput, toE164, isValidPhone } from '../utils/formatPhone'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Avatar,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  CircularProgress,
  Stack,
  Skeleton,
  Switch,
  FormControlLabel,
  FormHelperText,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { tabularNums } from '../theme'
import ErrorState from '../components/common/ErrorState'
import PageHelpButton from '../components/PageHelpButton'
import {
  Person as PersonIcon,
  Description as DescriptionIcon,
  LinkedIn as LinkedInIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as OutstandingIcon,
  Folder as FolderIcon,
  Language as PortalIcon,
  Assignment as TaskIcon,
  Flag as StageIcon,
  Star as NorthStarIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { useAtom } from 'jotai'
import { isAdminOrManager } from '../utils/roles'
import { CLIENT_TIER_OPTIONS, DEFAULT_CLIENT_TIER, isExecutiveClient, hasWildcardOutreach } from '../utils/clientTier'
import type { ClientTier } from '../utils/clientTier'
import { clientsApi, portalApi, attachmentsApi, Client, Attachment } from '../services/api'
import { useNotification } from '../hooks/useNotification'
import { userAtom } from '../state/auth'
import { workflowStageDefinitionsAtom, workflowStagesLoadingAtom } from '../state/clients'
import {
  buildPanelVisibility,
  type ClientRecordSectionId,
} from '../constants/stageVisibility'
import ClientResumeUploadTab from '../components/client-detail/ClientResumeUploadTab'
import CliftonStrengthsForm from '../components/client-detail/CliftonStrengthsForm'
import DreamJobForm from '../components/client-detail/DreamJobForm'
import ResumeTab from '../components/client-detail/ResumeTab'
import ClientTasksTab from '../components/client-detail/ClientTasksTab'
import CommentsTab from '../components/client-detail/CommentsTab'
import AttachmentsTab from '../components/client-detail/AttachmentsTab'
import NorthStarApprovalStatus from '../components/client-detail/NorthStarApprovalStatus'
import StageTasksChecklist from '../components/client-detail/StageTasksChecklist'
import AdvanceStageDialog from '../components/client-detail/AdvanceStageDialog'
import RelatedRecords from '../components/client-detail/RelatedRecords'
import { useWebSocket } from '../hooks/useWebSocket'
import { mergeLiveClient } from '../utils/liveClient'
import ClientTeamRow from '../components/client-detail/ClientTeamRow'
import AssignTeamDialog from '../components/clients/AssignTeamDialog'
import { canAssignTeam } from '../utils/clientTeam'

// Annotated against the panel catalogue so the two cannot drift: adding a
// section here without adding its panels there is a compile error.
const SECTIONS: readonly {
  id: ClientRecordSectionId
  label: string
  Icon: typeof StageIcon
}[] = [
  { id: 'stage', label: 'Stage', Icon: StageIcon },
  { id: 'profile', label: 'Profile', Icon: PersonIcon },
  { id: 'resume', label: 'Resume', Icon: DescriptionIcon },
  { id: 'linkedin', label: 'LinkedIn', Icon: LinkedInIcon },
  { id: 'north-star', label: 'North Star', Icon: NorthStarIcon },
  { id: 'activity', label: 'Activity', Icon: TaskIcon },
  { id: 'files', label: 'Files', Icon: FolderIcon },
] as const

type SectionId = ClientRecordSectionId

// ?tab= slug to record section.
//
// The record reads as one page now, so a slug scrolls to a section instead of
// switching a panel. Every slug the tabbed layouts ever published still
// resolves: the first seven name sections directly, the rest are slugs the
// nine-tab and five-tab layouts used, which were deep-linked from other pages
// (Tasks.tsx still sends ?tab=tasks) and pasted around.
const slugToSection: Record<string, SectionId> = {
  stage: 'stage',
  profile: 'profile',
  resume: 'resume',
  linkedin: 'linkedin',
  'north-star': 'north-star',
  activity: 'activity',
  files: 'files',

  strengths: 'profile',
  'dream-job': 'profile',
  'client-resume': 'resume',
  tasks: 'activity',
  comments: 'activity',
}

// The page owns its own scroll container, because every page in this app renders
// inside a flex root with overflow:hidden (components/Layout.tsx). That root is
// the scrollport position:sticky resolves against, and it never scrolls, so
// sticky anchored to the document has nothing to stick to. The offset is the
// shell's own geometry — main's mt:8 (64px) plus its p:3 top and bottom (48px) —
// so this container ends at the viewport floor and the document never scrolls.
const RECORD_HEIGHT = 'calc(100dvh - 112px)'

// How often a deep link checks whether its target has moved, how much quiet
// ends the landing, and the hard limit on the whole thing. Measured against the
// dev record: the old-resume PDF viewer and the LinkedIn preview finish
// expanding about three and a half seconds after first paint, which is well
// past any fixed budget short enough to feel safe.
const LANDING_TICK_MS = 100
const LANDING_QUIET_MS = 2500
const LANDING_LIMIT_MS = 12000

// A status is settled when nobody has to act on it again.
const SETTLED_STATUSES = new Set(['complete', 'not_needed'])

// resumeStatus/linkedinStatus are derived in the backend from publish + approval
// state (see MWA-10). In the deliverables stages we keep the more human prose
// ("client approved" / "pending approval").
function deriveResumeStatus(
  status: string | undefined,
  stage: string | undefined
): { label: string; settled: boolean } {
  const s = status || 'not_started'
  if (stage === 'deliverables_in_progress' || stage === 'deliverables_complete') {
    switch (s) {
      case 'complete':    return { label: 'client approved', settled: true }
      case 'in_review':   return { label: 'pending approval', settled: false }
      case 'in_progress': return { label: 'in progress', settled: false }
      case 'not_started': return { label: 'not started', settled: false }
    }
  }
  return { label: s.replace(/_/g, ' '), settled: SETTLED_STATUSES.has(s) }
}

function deriveLinkedinStatus(status: string | undefined): { label: string; settled: boolean } {
  const s = status || 'not_started'
  return { label: s.replace(/_/g, ' '), settled: SETTLED_STATUSES.has(s) }
}

// Fallback labels - dynamic labels from workflowStageDefinitionsAtom are preferred
const fallbackStageLabels: Record<string, string> = {
  onboarding: 'Onboarding',
  pre_north_star: 'Initial Consultation',
  north_star: 'North Star',
  strategy_session: 'Strategy Session',
  deliverables_in_progress: 'Deliverables in Progress',
  deliverables_complete: 'Deliverables Complete',
  linkedin_workshop: 'LinkedIn Workshop',
  market_ready: 'Market Ready',
  complete: 'Complete',
}

/**
 * One piece of state, read as a word rather than a colour. Settled is quiet;
 * outstanding is the only thing besides the active section that spends blue.
 */
function StatusReadout({
  label,
  value,
  settled,
}: {
  label?: string
  value: string
  settled: boolean
}) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {settled ? (
        <CheckCircleIcon fontSize="small" sx={{ color: 'success.dark' }} />
      ) : (
        <OutstandingIcon fontSize="small" sx={{ color: 'primary.main' }} />
      )}
      <Typography variant="body2" sx={{ color: settled ? 'text.secondary' : 'primary.dark' }}>
        {label ? `${label} — ${value}` : value}
      </Typography>
    </Stack>
  )
}

function RecordSection({
  id,
  title,
  aside,
  registerRef,
  children,
}: {
  id: SectionId
  title: string
  aside?: React.ReactNode
  registerRef: (id: SectionId, el: HTMLElement | null) => void
  children: React.ReactNode
}) {
  return (
    // A card rather than a rule between sections: on one long scrollable
    // record, a divider alone left staff unable to tell where a section ended.
    // The page sits on background.default, so paper reads as a distinct block.
    <Paper
      component="section"
      elevation={0}
      id={`record-${id}`}
      aria-labelledby={`record-${id}-heading`}
      ref={(el: HTMLElement | null) => {
        registerRef(id, el)
      }}
      sx={{
        p: { xs: 2, md: 3 },
        mb: { xs: 2, md: 3 },
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap', mb: 3 }}
      >
        <Typography id={`record-${id}-heading`} variant="h5" component="h2">
          {title}
        </Typography>
        {aside}
      </Stack>
      {children}
    </Paper>
  )
}

function Subsection({
  title,
  aside,
  children,
}: {
  title: string
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap', mb: 2 }}
      >
        <Typography variant="h6" component="h3">
          {title}
        </Typography>
        {aside}
      </Stack>
      {children}
    </Box>
  )
}

// Extend Client type to include portal fields
interface ClientWithPortal extends Client {
  portalEnabled?: boolean
  portalUserEmail?: string
  portalEnabledAt?: string
}

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotification()
  const [user] = useAtom(userAtom)
  const [workflowStages] = useAtom(workflowStageDefinitionsAtom)
  const [workflowStagesLoading] = useAtom(workflowStagesLoadingAtom)
  const theme = useTheme()
  const wideSpine = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true })
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true })

  // Build stage labels dynamically from DB definitions, with hardcoded fallback
  const stageLabels = useMemo(() => {
    if (workflowStages.length > 0) {
      return Object.fromEntries(workflowStages.filter((s) => s.isActive).map((s) => [s.stageId, s.label]))
    }
    return fallbackStageLabels
  }, [workflowStages])

  // Active stage IDs in order, derived strictly from the DB-driven atom.
  // Used by getNextStage so the FE never asks the backend to advance to an inactive stage.
  const activeStageOrder = useMemo(
    () =>
      [...workflowStages]
        .filter((s) => s.isActive)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
        .map((s) => s.stageId),
    [workflowStages]
  )

  const getNextStage = useCallback(
    (currentStage: string): string | null => {
      const currentIndex = activeStageOrder.indexOf(currentStage)
      if (currentIndex === -1 || currentIndex >= activeStageOrder.length - 1) {
        return null
      }
      return activeStageOrder[currentIndex + 1]
    },
    [activeStageOrder]
  )
  const [client, setClient] = useState<ClientWithPortal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Where the reader is in the record. An unrecognised ?tab= slug is not an
  // error: it opens the record at the top, exactly as arriving without one does.
  const requestedSection = slugToSection[searchParams.get('tab') || '']
  const [activeSection, setActiveSection] = useState<SectionId>(requestedSection || 'stage')
  // The section a slug asked for, held until the landing below has finished.
  // While it is set it is also what the spine highlights: where the reader
  // actually is cannot be read off the scroll position until the content above
  // the target has stopped moving.
  const [landingTarget, setLandingTarget] = useState<SectionId | null>(requestedSection || null)
  const landingRef = useRef<SectionId | null>(requestedSection || null)
  const landingLimit = useRef(0)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const recordRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLDivElement | null>(null)
  const sectionRefs = useRef<Partial<Record<SectionId, HTMLElement | null>>>({})
  const [headerHeight, setHeaderHeight] = useState(0)

  // Ends the landing for good. The ref is what the callbacks read, so that a
  // release takes effect before React has re-rendered.
  const endLanding = useCallback(() => {
    if (!landingRef.current) return
    landingRef.current = null
    setLandingTarget(null)
  }, [])

  const [portalDialogOpen, setPortalDialogOpen] = useState(false)
  const [portalEmail, setPortalEmail] = useState('')
  const [enablingPortal, setEnablingPortal] = useState(false)
  const [resendingInvite, setResendingInvite] = useState(false)
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false)
  const [assignTeamOpen, setAssignTeamOpen] = useState(false)
  const [stageTasksComplete, setStageTasksComplete] = useState<boolean | null>(null)

  // North Star publish confirmation state
  const [northStarPublishDialogOpen, setNorthStarPublishDialogOpen] = useState(false)
  const [pendingPublishAttachment, setPendingPublishAttachment] = useState<Attachment | null>(null)
  const [publishingNorthStar, setPublishingNorthStar] = useState(false)
  const [northStarRefreshKey, setNorthStarRefreshKey] = useState(0)

  // Edit client info dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editPhone, setEditPhone] = useState('')
  const [editClientTier, setEditClientTier] = useState<ClientTier>(DEFAULT_CLIENT_TIER)
  const [editWildcardOutreach, setEditWildcardOutreach] = useState(false)
  const [saving, setSaving] = useState(false)

  // Service agreement approval state
  const [savingServiceAgreement, setSavingServiceAgreement] = useState(false)

  useEffect(() => {
    if (id) {
      loadClient()
    }
  }, [id])

  const loadClient = async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const data = await clientsApi.get(id)
      setClient(data)
    } catch (err) {
      console.error('Error loading client:', err)
      setError(err instanceof Error ? err.message : 'Failed to load client')
    } finally {
      setLoading(false)
    }
  }

  // MWA-390 — the open record follows changes made elsewhere (another staff member, the
  // client in the portal, an email arriving) without a reload. Messages about other
  // clients are ignored, and an unchanged record keeps its identity so nothing re-renders.
  const liveClientHandlers = useMemo(() => {
    const merge = (message: { data: unknown }) =>
      setClient((prev) => (prev ? mergeLiveClient(prev, message.data as Partial<ClientWithPortal>) : prev))
    return {
      client_updated: merge,
      workflow_advanced: merge,
      // Anything broadcast while the socket was down is missed: read the record again,
      // quietly, so the page does not swap itself for its loading skeleton.
      ws_reconnected: () => {
        if (!id) return
        clientsApi
          .get(id)
          .then((fresh) => setClient((prev) => (prev ? mergeLiveClient(prev, fresh as Partial<ClientWithPortal>) : fresh)))
          .catch((err) => console.error('Error refreshing client after reconnect:', err))
      },
    }
  }, [id])
  useWebSocket(liveClientHandlers)

  const registerSectionRef = useCallback((sectionId: SectionId, el: HTMLElement | null) => {
    sectionRefs.current[sectionId] = el
  }, [])

  // The pinned bar is measured rather than assumed: it grows a second line when
  // the spine folds into a rail, and both the scroll offsets and the spine's own
  // sticky top are read off it.
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const measure = () => setHeaderHeight(el.getBoundingClientRect().height)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [loading, wideSpine])

  // Where the container would have to sit for this section to start just under
  // the pinned bar, clamped to what the record can actually scroll to. Read
  // live off the element every time, because everything above it is still
  // free to change height.
  const sectionTop = useCallback(
    (sectionId: SectionId) => {
      const container = scrollRef.current
      const el = sectionRefs.current[sectionId]
      if (!container || !el) return null
      const gap = parseFloat(theme.spacing(2))
      const offset =
        container.scrollTop +
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top -
        headerHeight -
        gap
      const max = container.scrollHeight - container.clientHeight
      return Math.max(0, Math.min(Math.round(offset), Math.round(max)))
    },
    [headerHeight, theme]
  )

  const scrollToSection = useCallback(
    (sectionId: SectionId, behavior: ScrollBehavior) => {
      const container = scrollRef.current
      const top = sectionTop(sectionId)
      if (!container || top === null) return
      container.scrollTo({ top, behavior })
    },
    [sectionTop]
  )

  const handleSelectSection = useCallback(
    (sectionId: SectionId) => {
      endLanding()
      setActiveSection(sectionId)
      scrollToSection(sectionId, reduceMotion ? 'auto' : 'smooth')
    },
    [endLanding, reduceMotion, scrollToSection]
  )

  // Follow the reader down the record. The URL is rewritten, never pushed, so
  // reading a client record leaves the back button where it was. A legacy slug
  // that already resolves to this section is left alone rather than rewritten
  // out from under whoever pasted it.
  useEffect(() => {
    if (loading) return
    const currentSlug = new URLSearchParams(window.location.search).get('tab')
    if (currentSlug && slugToSection[currentSlug] === activeSection) return
    const next = new URLSearchParams(window.location.search)
    next.set('tab', activeSection)
    setSearchParams(next, { replace: true })
  }, [activeSection, loading, setSearchParams])

  useEffect(() => {
    const container = scrollRef.current
    if (loading || !container) return
    let frame = 0
    const read = () => {
      frame = 0
      // A landing still in progress owns the highlight. Reading it off the
      // scroll position while the sections above the target are still growing
      // is what had the spine naming a section the reader was not looking at.
      if (landingRef.current) {
        setActiveSection(landingRef.current)
        return
      }
      // At the floor of the record the last section is current even if it is
      // too short to reach the marker line, so jumping to it from the spine
      // does not bounce the marker back to the section above.
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
        // Read the last section that actually rendered rather than the last
        // in the catalogue — a stage can hide the trailing sections, and
        // naming one of those would highlight a spine entry that is not there.
        const rendered = SECTIONS.filter((section) => sectionRefs.current[section.id])
        if (rendered.length > 0) {
          setActiveSection(rendered[rendered.length - 1].id)
          return
        }
      }
      // The marker line has to sit where a section actually comes to rest when
      // the spine or a slug scrolls to it — under the pinned bar and the same
      // breathing gap. A line drawn tight against the bar is above the section
      // it just scrolled to, so the spine names the section above it instead.
      const line =
        container.getBoundingClientRect().top + headerHeight + parseFloat(theme.spacing(2)) + 1
      let current: SectionId = SECTIONS[0].id
      for (const section of SECTIONS) {
        const el = sectionRefs.current[section.id]
        if (el && el.getBoundingClientRect().top <= line) current = section.id
      }
      setActiveSection(current)
    }
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(read)
    }
    // Read once on subscribe, so the highlight is correct on arrival and is
    // handed back from the landing to the scroll position the moment the
    // landing releases it.
    read()
    container.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [loading, headerHeight, landingTarget, theme])

  // A deep link has to survive everything above the target finishing its own
  // loading. Tasks, comments and attachment lists arrive after first paint, and
  // the old resume's PDF viewer and the LinkedIn preview keep growing for
  // seconds after that — each one pushes the target further down the record. So
  // the landing is not one scroll but a claim, re-asserted from the target's
  // own live position for as long as that position keeps moving — watched both
  // by a resize observer, which corrects in the frame the layout changes, and
  // by a ticker, which is what decides the position has settled and is the only
  // one of the two that survives a browser not currently painting this page.
  // It gives up the moment the reader takes over, because taking the scroll
  // back from someone who has started reading is worse than landing short, and
  // it gives up at a hard limit so that a surface which never stops resizing
  // cannot hold the scroll hostage.
  useEffect(() => {
    const container = scrollRef.current
    const column = recordRef.current
    if (loading || !container || !column || !landingTarget) return

    if (!landingLimit.current) landingLimit.current = Date.now() + LANDING_LIMIT_MS

    let movedAt = Date.now()
    const detach = () => {
      window.clearInterval(ticker)
      observer.disconnect()
      container.removeEventListener('wheel', release)
      container.removeEventListener('touchmove', release)
      container.removeEventListener('mousedown', release)
      window.removeEventListener('keydown', release)
    }
    const release = () => {
      detach()
      endLanding()
    }
    // Correct the position only when the target has actually moved away from
    // it. That difference going quiet is the end condition: nothing above the
    // target is growing any more.
    const reassert = () => {
      const top = sectionTop(landingTarget)
      if (top === null || Math.abs(top - container.scrollTop) <= 1) return
      container.scrollTo({ top, behavior: 'auto' })
      movedAt = Date.now()
    }
    const observer = new ResizeObserver(reassert)
    const ticker = window.setInterval(() => {
      if (Date.now() > landingLimit.current) {
        release()
        return
      }
      reassert()
      if (Date.now() - movedAt > LANDING_QUIET_MS) release()
    }, LANDING_TICK_MS)

    reassert()
    observer.observe(column)
    container.addEventListener('wheel', release, { passive: true })
    container.addEventListener('touchmove', release, { passive: true })
    container.addEventListener('mousedown', release)
    window.addEventListener('keydown', release)
    // Only the listeners come down when this re-runs (the measured header
    // height changes once, early); the landing itself ends only on release.
    return detach
  }, [endLanding, landingTarget, loading, sectionTop])

  // MUI locks document.body while an overlay is open, so that the page cannot
  // scroll out from under a menu that positions itself once, at the moment it
  // opens. This record's scrollport is its own container rather than the
  // document, so that lock misses it and an open select was left floating over
  // whatever the container scrolled to. Lock the container for as long as an
  // overlay is mounted, which is the same bargain MUI strikes with the
  // document, and leave closing it to MUI's own Escape and backdrop contract.
  // The scrollbar gutter is reserved permanently, so taking the scrollbar away
  // moves nothing.
  useEffect(() => {
    const container = scrollRef.current
    if (loading || !container) return
    const sync = () => {
      const overlayOpen = Array.from(document.body.children).some((el) =>
        el.matches('.MuiModal-root:not(.MuiModal-hidden)')
      )
      container.style.overflowY = overlayOpen ? 'hidden' : ''
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.body, { childList: true })
    return () => {
      observer.disconnect()
      container.style.overflowY = ''
    }
  }, [loading])

  const handleClientUpdate = (updatedClient: Partial<Client>) => {
    if (client) {
      setClient({ ...client, ...updatedClient })
    }
  }

  const handleOpenPortalDialog = () => {
    setPortalEmail(client?.email || '')
    setPortalDialogOpen(true)
  }

  const handleEnablePortal = async () => {
    if (!client?.id) return

    setEnablingPortal(true)
    try {
      const result = await portalApi.createClientUser(client.id, portalEmail || undefined)
      setClient({ ...client, ...result.client })
      showSuccess('Portal access enabled. An invite email has been sent to the client.')
      setPortalDialogOpen(false)
    } catch (err) {
      console.error('Failed to enable portal:', err)
      showError(err instanceof Error ? err.message : 'Failed to enable portal access')
    } finally {
      setEnablingPortal(false)
    }
  }

  // MWA-225 — resend the portal invite for an already-enabled client. Hits the
  // same create-client-user endpoint, which detects the enabled portal and
  // re-sends the Cognito invite (resetting the invite link's expiry). Repeatable.
  const handleResendInvite = async () => {
    if (!client?.id) return

    setResendingInvite(true)
    try {
      await portalApi.createClientUser(client.id, client.portalUserEmail || client.email || undefined)
      showSuccess('Invitation re-sent. The client will receive a fresh invite email.')
    } catch (err) {
      console.error('Failed to resend invite:', err)
      showError(err instanceof Error ? err.message : 'Failed to resend invitation')
    } finally {
      setResendingInvite(false)
    }
  }

  const handleOpenEditDialog = () => {
    if (client) {
      setEditName(client.name || '')
      setEditEmail(client.email || '')
      setEditPhone(client.phone || '')
      setEditClientTier(client.clientTier ?? DEFAULT_CLIENT_TIER)
      setEditWildcardOutreach(hasWildcardOutreach(client))
      setEditDialogOpen(true)
    }
  }

  const handleSaveClientInfo = async () => {
    if (!client?.id) return

    setSaving(true)
    try {
      const updatedClient = await clientsApi.update({
        id: client.id,
        name: editName.trim(),
        email: editEmail.trim(),
        phone: editPhone.trim() ? toE164(editPhone) : undefined,
        clientTier: editClientTier,
        wildcardOutreachEnabled: editWildcardOutreach,
      })
      setClient({ ...client, ...updatedClient })
      showSuccess('Client info updated')
      setEditDialogOpen(false)
    } catch (err) {
      console.error('Failed to update client:', err)
      showError(err instanceof Error ? err.message : 'Failed to update client info')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleServiceAgreement = async (approved: boolean) => {
    if (!client?.id) return
    setSavingServiceAgreement(true)
    try {
      const updatedClient = await clientsApi.update({
        id: client.id,
        serviceAgreementApproved: approved,
        serviceAgreementApprovedAt: approved ? new Date().toISOString() : undefined,
        serviceAgreementApprovedBy: approved ? (user?.email || '') : undefined,
      })
      setClient({ ...client, ...updatedClient })
      showSuccess(
        approved
          ? 'Service agreement marked as approved'
          : 'Service agreement approval cleared'
      )
    } catch (err) {
      console.error('Failed to update service agreement:', err)
      showError(err instanceof Error ? err.message : 'Failed to update service agreement')
    } finally {
      setSavingServiceAgreement(false)
    }
  }

  const handleOpenAssignTeam = () => {
    setAssignTeamOpen(true)
  }

  const handleTeamAssigned = async (updatedClient: Client) => {
    setClient(prevClient => prevClient ? { ...prevClient, ...updatedClient } : updatedClient)
    showSuccess('Team assigned successfully')
    setAssignTeamOpen(false)
    // Re-read quietly: loadClient would swap the page for its skeleton and
    // unmount the Advance Stage dialog this was opened from.
    try {
      const fresh = await clientsApi.get(updatedClient.id)
      setClient((prevClient) => (prevClient ? { ...prevClient, ...fresh } : fresh))
    } catch (err) {
      console.error('Error refreshing client after team assignment:', err)
    }
  }

  const handleStageTasksChange = useCallback((allRequiredComplete: boolean) => {
    setStageTasksComplete(allRequiredComplete)
  }, [])

  const handleNorthStarPublish = (attachment: Attachment) => {
    setPendingPublishAttachment(attachment)
    setNorthStarPublishDialogOpen(true)
  }

  const handleConfirmNorthStarPublish = async () => {
    if (!pendingPublishAttachment || !client) return

    setPublishingNorthStar(true)
    try {
      // Publish the attachment — backend automatically sends notification email
      await attachmentsApi.publish(pendingPublishAttachment.id)

      setNorthStarRefreshKey((prev) => prev + 1)
      showSuccess('North Star published. Client notified.')
    } catch (err) {
      console.error('Failed to publish North Star:', err)
      showError('Failed to publish North Star document')
    } finally {
      setPublishingNorthStar(false)
      setNorthStarPublishDialogOpen(false)
      setPendingPublishAttachment(null)
    }
  }

  // Which parts of the record this stage shows. Configured in Stage Gates;
  // an unconfigured stage — every stage until an admin says otherwise —
  // shows the whole record. Derived above the early returns because the
  // retarget effect below has to depend on it.
  const stage = client?.workflowStage || 'onboarding'
  const stageHiddenPanels = useMemo(
    () => workflowStages.find((s) => s.stageId === stage)?.hiddenPanels,
    [workflowStages, stage]
  )
  const { isPanelVisible, isSectionVisible } = useMemo(
    () => buildPanelVisibility(stageHiddenPanels),
    [stageHiddenPanels]
  )
  const visibleSections = useMemo(
    () => SECTIONS.filter((section) => isSectionVisible(section.id)),
    [isSectionVisible]
  )

  // A deep link can name a section this stage hides — Tasks.tsx sends
  // ?tab=tasks from five call sites. Without this the landing chases an
  // element that never mounts: nothing scrolls, and the spine highlights
  // nothing at all until the landing times out.
  useEffect(() => {
    if (loading || !client) return
    if (visibleSections.some((section) => section.id === activeSection)) return
    const fallback = visibleSections[0]?.id
    if (!fallback) return
    landingRef.current = null
    setLandingTarget(null)
    setActiveSection(fallback)
  }, [loading, client, visibleSections, activeSection])

  // The stage list and the client record are fetched in the same tick, so
  // rendering before the configuration lands would mount every gated panel,
  // fire its requests, and then unmount it — a visible flash of content an
  // admin chose to hide, and enough layout churn to make a deep link land
  // short. A failed stage load falls through with an empty list, which shows
  // the whole record: for a deny list, showing too much beats hiding the
  // record behind a transient network error.
  // The header's own shape rather than a centred spinner. The spinner threw the
  // page away and rebuilt it, so the section spine jumped into place a beat
  // after the name did.
  if (loading || workflowStagesLoading) {
    return (
      <Box sx={{ height: RECORD_HEIGHT, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={220} height={26} />
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Skeleton variant="rounded" width={110} height={22} />
                <Skeleton variant="text" width={120} height={20} />
              </Stack>
            </Box>
            <Skeleton variant="rounded" width={132} height={44} />
          </Stack>
        </Box>
        <Stack direction="row" spacing={2} sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Skeleton variant="text" width={260} height={20} />
          <Box sx={{ flexGrow: 1 }} />
          <Skeleton variant="rounded" width={140} height={44} />
        </Stack>
        <Box sx={{ pt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rounded" height={180} />
          <Skeleton variant="rounded" height={240} />
        </Box>
      </Box>
    )
  }

  if (error || !client) {
    return (
      <Box sx={{ pt: 4 }}>
        <ErrorState
          title={error ? 'Couldn’t load this client' : 'Client not found'}
          description={
            error
              ? 'The client record didn’t load. Nothing has been changed — try again, and tell us if it keeps happening.'
              : 'This client record does not exist, or it has been removed.'
          }
          detail={error || undefined}
          onRetry={error ? () => window.location.reload() : undefined}
        />
      </Box>
    )
  }

  const stageLabel = stageLabels[stage] || stage
  const resumeStatus = deriveResumeStatus(client.resumeStatus, stage)
  const linkedinStatus = deriveLinkedinStatus(client.linkedinStatus)
  const strengthCount = client.cliftonStrengths?.length || 0
  const serviceAgreementApproved = !!client.serviceAgreementApproved
  const serviceAgreementNeeded = stage === 'onboarding'

  const outstandingBySection: Record<SectionId, number> = {
    stage:
      (client.intakeCompletedAt ? 0 : 1) +
      (serviceAgreementNeeded && !serviceAgreementApproved ? 1 : 0) +
      (stageTasksComplete === false ? 1 : 0),
    // An outstanding item whose panel this stage hides is work nobody can
    // reach from here, and a count promising it is worse than no count.
    profile:
      (isPanelVisible('clifton-strengths') && !client.cliftonStrengthsCompletedAt ? 1 : 0) +
      (isPanelVisible('dream-job') && !client.dreamJobCompletedAt ? 1 : 0),
    resume: isPanelVisible('resume-new') && !resumeStatus.settled ? 1 : 0,
    linkedin: isPanelVisible('linkedin') && !linkedinStatus.settled ? 1 : 0,
    'north-star': 0,
    activity: 0,
    files: 0,
  }
  const outstandingCount = visibleSections.reduce(
    (total, section) => total + outstandingBySection[section.id],
    0
  )

  const strengthsValue =
    [
      strengthCount ? `${strengthCount}/5+` : null,
      client.cliftonStrengthsCompletedAt ? 'Complete' : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'Not started'

  const spine = (
    <Box component="nav" aria-label="Client record sections">
      <Stack
        direction={wideSpine ? 'column' : 'row'}
        spacing={wideSpine ? 0.25 : 0.5}
        useFlexGap
        sx={{ flexWrap: wideSpine ? 'nowrap' : 'wrap' }}
      >
        {visibleSections.map(({ id: sectionId, label, Icon }) => {
          const count = outstandingBySection[sectionId]
          const selected = activeSection === sectionId
          return (
            <Button
              key={sectionId}
              onClick={() => handleSelectSection(sectionId)}
              aria-current={selected ? 'true' : undefined}
              aria-label={count > 0 ? `${label}, ${count} outstanding` : label}
              startIcon={<Icon fontSize="small" />}
              sx={{
                justifyContent: wideSpine ? 'flex-start' : 'center',
                minWidth: 0,
                // Below the wide breakpoint the spine sits inside the sticky
                // header and is the only way between sections, so it is a tap
                // target rather than a dense list.
                minHeight: 44,
                px: 1.25,
                color: selected ? 'primary.dark' : 'text.secondary',
                fontWeight: selected ? 700 : 500,
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                '&:hover': {
                  bgcolor: selected
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.text.primary, 0.04),
                },
              }}
            >
              {label}
              {count > 0 && (
                <Typography
                  component="span"
                  variant="caption"
                  aria-hidden="true"
                  sx={{
                    ml: wideSpine ? 'auto' : 0.75,
                    pl: 1,
                    color: 'primary.dark',
                    fontWeight: 700,
                  }}
                >
                  {count}
                </Typography>
              )}
            </Button>
          )
        })}
      </Stack>
    </Box>
  )

  return (
    <Box
      ref={scrollRef}
      sx={{
        position: 'relative',
        height: RECORD_HEIGHT,
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarGutter: 'stable',
      }}
    >
      <Box
        ref={headerRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 3,
          bgcolor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {/* Two levels, not one row of equally-weighted controls: who this is,
            then what is true of them. The name was a h6 sharing a line with a
            chip, an icon button and a count, so nothing in the header read as
            the title of the page. */}
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1.5}
          useFlexGap
          sx={{ py: 1, minWidth: 0, flexWrap: 'wrap' }}
        >
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', typography: 'subtitle2', flexShrink: 0 }}>
            {client.name?.[0] || '?'}
          </Avatar>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {/* Wraps rather than truncating. A header that ellipses a
                  client's name to protect its own layout has failed at the one
                  job it has. */}
              <Typography variant="h4" component="h1" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                {client.name}
              </Typography>
              <Tooltip title="Edit client info">
                <IconButton
                  aria-label="Edit client info"
                  onClick={handleOpenEditDialog}
                  sx={{ width: 44, height: 44 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              useFlexGap
              sx={{ flexWrap: 'wrap', mt: 0.25 }}
            >
              {/* Stage labels are Stage Gates admin config, so their length is not
                  ours to predict; cap it so one long label cannot break the line. */}
              <Chip
                label={stageLabel}
                size="small"
                variant="outlined"
                sx={{ maxWidth: 220, flexShrink: 0 }}
              />
              {/* MWA-308 — only the executive tier is called out. Standard is the
                  default for every unclassified client, so chipping it too would
                  label almost every record without telling anyone anything. */}
              {isExecutiveClient(client) && (
                <Chip
                  label="Executive"
                  size="small"
                  color="primary"
                  sx={{ flexShrink: 0 }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: outstandingCount > 0 ? 'primary.dark' : 'text.secondary',
                  fontWeight: outstandingCount > 0 ? 600 : 400,
                  ...tabularNums,
                }}
              >
                {outstandingCount > 0 ? `${outstandingCount} outstanding` : 'Nothing outstanding'}
              </Typography>
            </Stack>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: 'wrap', flexShrink: 0 }}
          >
            <PageHelpButton articleId="page-client-detail" />
            {stage !== 'complete' && isAdminOrManager(user) && (
              <Button
                variant="contained"
                onClick={() => setAdvanceDialogOpen(true)}
                sx={{ minHeight: 44 }}
              >
                Advance Stage
              </Button>
            )}
          </Stack>
        </Stack>
        {!wideSpine && <Box sx={{ pb: 1 }}>{spine}</Box>}
      </Box>

      {/* Where this client's work lives on other pages. Sits under the name
          rather than in the spine, which scrolls the record instead of leaving
          it. */}
      <Box sx={{ pt: 1 }}>
        <RelatedRecords client={client} />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap', py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
          {client.email}
          {client.phone && ` • ${formatPhoneNumber(client.phone)}`}
        </Typography>
        {client.clientSuccess && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.dark', typography: 'caption' }}>
              {client.clientSuccess.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" color="text.secondary">
              Client Success: {client.clientSuccess.split('@')[0]}
            </Typography>
          </Stack>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {client.portalEnabled ? (
          <>
            <StatusReadout value="Portal enabled" settled />
            {/* MWA-225 — repeatable invite resend for busy clients who let the link lapse */}
            <Tooltip title="Re-send the portal invite email and reset its expiry">
              <span>
                <Button
                  variant="outlined"
                  startIcon={resendingInvite ? <CircularProgress size={16} /> : <PortalIcon />}
                  onClick={handleResendInvite}
                  disabled={resendingInvite}
                  sx={{ minHeight: 44 }}
                >
                  Resend Invite
                </Button>
              </span>
            </Tooltip>
          </>
        ) : (
          <Button
            variant="outlined"
            startIcon={<PortalIcon />}
            onClick={handleOpenPortalDialog}
            sx={{ minHeight: 44 }}
          >
            Enable Portal
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: wideSpine ? 'minmax(180px, 220px) minmax(0, 1fr)' : 'minmax(0, 1fr)',
          columnGap: 4,
        }}
      >
        {wideSpine && (
          <Box sx={{ borderRight: 1, borderColor: 'divider', pr: 2 }}>
            <Box sx={{ position: 'sticky', top: headerHeight, pt: 4 }}>{spine}</Box>
          </Box>
        )}

        <Box ref={recordRef} sx={{ pt: 4, minWidth: 0 }}>
          {/* What it takes to move this client forward: the gates, then the
              stage's own checklist. */}
          <RecordSection id="stage" title="Stage" registerRef={registerSectionRef}>
            <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap', mb: 3 }}>
              <StatusReadout
                label="Client Intake"
                value={client.intakeCompletedAt ? 'Complete' : 'Not started'}
                settled={!!client.intakeCompletedAt}
              />
            </Stack>

            {serviceAgreementNeeded && (
              <>
                <Divider sx={{ mb: 3 }} />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}
                  useFlexGap
                  sx={{ flexWrap: 'wrap', mb: 3 }}
                >
                  <Box sx={{ maxWidth: '68ch' }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 0.5 }}>
                      Service Agreement
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Client must approve the proposal / service agreement before onboarding can
                      proceed.
                    </Typography>
                    <StatusReadout
                      value={serviceAgreementApproved ? 'Approved' : 'Pending'}
                      settled={serviceAgreementApproved}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    useFlexGap
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {serviceAgreementApproved ? (
                      <>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Approved{' '}
                            {client.serviceAgreementApprovedAt
                              ? new Date(client.serviceAgreementApprovedAt).toLocaleString()
                              : ''}
                          </Typography>
                          {client.serviceAgreementApprovedBy && (
                            <Typography variant="body2" color="text.secondary">
                              by {client.serviceAgreementApprovedBy}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          color="inherit"
                          disabled={savingServiceAgreement}
                          onClick={() => handleToggleServiceAgreement(false)}
                        >
                          {savingServiceAgreement ? <CircularProgress size={16} /> : 'Unapprove'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={
                          savingServiceAgreement ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <CheckCircleIcon />
                          )
                        }
                        disabled={savingServiceAgreement}
                        onClick={() => handleToggleServiceAgreement(true)}
                      >
                        Mark as Approved
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <Divider sx={{ mb: 3 }} />
              </>
            )}

            {!serviceAgreementNeeded && <Divider sx={{ mb: 3 }} />}
            <ClientTeamRow
              team={{
                clientSuccess: client.clientSuccess,
                resumeWriter: client.resumeWriter,
                coach: client.coach,
              }}
              onAssign={canAssignTeam(user) ? handleOpenAssignTeam : undefined}
            />
            <Divider sx={{ mb: 3 }} />

            <StageTasksChecklist
              key={`${client.id}-${stage}`}
              clientId={client.id}
              stageId={stage}
              stageName={stageLabel}
              onTasksChange={handleStageTasksChange}
              clientName={client.name}
              workflowStage={stage}
              onStageAdvanced={loadClient}
            />
          </RecordSection>

          {/* Who the client is: what they are good at, and what they want. */}
          {isSectionVisible('profile') && (
          <RecordSection id="profile" title="Profile" registerRef={registerSectionRef}>
            <Stack spacing={5} divider={<Divider />}>
              {isPanelVisible('clifton-strengths') && (
              <Subsection
                title="Clifton Strengths"
                aside={
                  <StatusReadout
                    value={strengthsValue}
                    settled={!!client.cliftonStrengthsCompletedAt}
                  />
                }
              >
                <CliftonStrengthsForm client={client} onUpdate={handleClientUpdate} />
              </Subsection>
              )}
              {isPanelVisible('dream-job') && (
              <Subsection
                title="Dream Job"
                aside={
                  <StatusReadout
                    value={client.dreamJobCompletedAt ? 'Complete' : 'Not started'}
                    settled={!!client.dreamJobCompletedAt}
                  />
                }
              >
                <DreamJobForm client={client} onUpdate={handleClientUpdate} />
              </Subsection>
              )}
            </Stack>
          </RecordSection>
          )}

          {/* The resume, input then output: what the client gave us, then what
              we wrote from it. These were two tabs whose labels and URL slugs
              disagreed with each other, which had staff opening the wrong one. */}
          {isSectionVisible('resume') && (
          <RecordSection
            id="resume"
            title="Resume"
            registerRef={registerSectionRef}
            aside={<StatusReadout value={resumeStatus.label} settled={resumeStatus.settled} />}
          >
            <Stack spacing={5} divider={<Divider />}>
              {isPanelVisible('resume-old') && (
              <Subsection title="Old Resume">
                <ClientResumeUploadTab client={client} onUpdate={handleClientUpdate} />
              </Subsection>
              )}
              {isPanelVisible('resume-new') && (
              <Subsection title="New Resume">
                <ResumeTab client={client} onUpdate={handleClientUpdate} />
              </Subsection>
              )}
            </Stack>
          </RecordSection>
          )}

          {isSectionVisible('linkedin') && (
          <RecordSection
            id="linkedin"
            title="LinkedIn"
            registerRef={registerSectionRef}
            aside={<StatusReadout value={linkedinStatus.label} settled={linkedinStatus.settled} />}
          >
            <AttachmentsTab
              entityId={client.id}
              entityType="linkedin"
              title="LinkedIn Content"
              emptyStateText="Upload LinkedIn profile content"
              client={client}
            />
          </RecordSection>
          )}

          {/* The strategy document the search is built on. It used to be an
              outlined button called "Open editor" inside Files, next to prose
              about a microsite — which is why staff could not find it. It is a
              deliverable, so it sits with the others and carries its own
              approval state. Hiding it until the client reaches North Star prep
              is Stage Gates configuration, not code. */}
          {isSectionVisible('north-star') && (
          <RecordSection
            id="north-star"
            title="North Star"
            registerRef={registerSectionRef}
            // Only admins and managers may read the North Star record, so for
            // anyone else there is no status to show.
            aside={
              isAdminOrManager(user) ? (
                <NorthStarApprovalStatus
                  key={client.id}
                  clientId={client.id}
                  northStarApproved={client.northStarApproved}
                  northStarApprovedAt={client.northStarApprovedAt}
                />
              ) : undefined
            }
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
              useFlexGap
              sx={{ flexWrap: 'wrap', mb: 3 }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>
                Built from this client&apos;s strengths, dream job and resume. The editor
                generates it and publishes it as a shareable microsite on its own link.
                The legacy documents below are the older route, and publishing one of
                those is what puts a North Star in the client&apos;s portal.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<NorthStarIcon />}
                onClick={() => navigate(`/clients/${client.id}/north-star`)}
              >
                Open North Star editor
              </Button>
            </Stack>
            <AttachmentsTab
              key={northStarRefreshKey}
              entityId={client.id}
              entityType="north_star"
              title="Legacy documents"
              emptyStateText="No legacy North Star PDFs. New North Stars are built in the editor."
              client={client}
              onPublishConfirm={handleNorthStarPublish}
            />
          </RecordSection>
          )}

          {/* Working with the client rather than producing for them. */}
          {isSectionVisible('activity') && (
          <RecordSection id="activity" title="Activity" registerRef={registerSectionRef}>
            <Stack spacing={5} divider={<Divider />}>
              {isPanelVisible('tasks') && (
              <Box>
                <ClientTasksTab client={client} />
              </Box>
              )}
              {isPanelVisible('comments') && (
              <Subsection title="Comments">
                <CommentsTab client={client} onUpdate={handleClientUpdate} />
              </Subsection>
              )}
            </Stack>
          </RecordSection>
          )}

          {isSectionVisible('files') && (
          <RecordSection id="files" title="Files" registerRef={registerSectionRef}>
            <AttachmentsTab
              entityId={client.id}
              entityType="client"
              title="Client Folder"
              fallbackEntityId={client.leadId}
              fallbackEntityType="lead"
              visibilityControls
              internalApproval
            />
          </RecordSection>
          )}
        </Box>
      </Box>

      {/* Enable Portal Dialog */}
      <Dialog open={portalDialogOpen} onClose={() => setPortalDialogOpen(false)}>
        <DialogTitle>Enable Client Portal Access</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will create a portal account for the client and send them an email invitation
            with login instructions. The client will be able to:
          </DialogContentText>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>Complete their intake forms</li>
            <li>View and complete assigned tasks</li>
            <li>Upload documents</li>
            <li>View their resume drafts</li>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Client Email"
            type="email"
            fullWidth
            variant="outlined"
            value={portalEmail}
            onChange={(e) => setPortalEmail(e.target.value)}
            helperText="The invitation will be sent to this email address"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortalDialogOpen(false)} disabled={enablingPortal}>
            Cancel
          </Button>
          <Button
            onClick={handleEnablePortal}
            variant="contained"
            disabled={enablingPortal || !portalEmail}
          >
            {enablingPortal ? <CircularProgress size={20} /> : 'Enable Portal Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advance Stage Dialog */}
      {getNextStage(stage) && (
        <AdvanceStageDialog
          open={advanceDialogOpen}
          onClose={() => setAdvanceDialogOpen(false)}
          clientId={client.id}
          clientName={client.name || 'Client'}
          currentStage={stage}
          targetStage={getNextStage(stage)!}
          currentStageLabel={stageLabel}
          targetStageLabel={stageLabels[getNextStage(stage)!] || getNextStage(stage)!}
          onAdvanced={() => {
            loadClient()
            showSuccess(`Client advanced to ${stageLabels[getNextStage(stage)!]}`)
          }}
          resumeApproved={client.resumeApproved}
          internalResumeApproved={client.internalResumeApproved}
          internalApprovedBy={client.internalApprovedBy}
          serviceAgreementApproved={client.serviceAgreementApproved}
          teamAssignment={{
            clientSuccess: client.clientSuccess,
            resumeWriter: client.resumeWriter,
            coach: client.coach,
          }}
          onAssignTeam={handleOpenAssignTeam}
          entryEmail={workflowStages.find((s) => s.stageId === getNextStage(stage))?.entryEmail ?? null}
        />
      )}

      {/* Assign Team Dialog */}
      <AssignTeamDialog
        open={assignTeamOpen}
        onClose={() => setAssignTeamOpen(false)}
        client={client}
        onAssigned={handleTeamAssigned}
      />

      {/* Edit Client Info Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Client Info</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={editEmail}
            onChange={(e) => setEditEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Phone"
            fullWidth
            value={formatPhoneInput(editPhone)}
            onChange={(e) => setEditPhone(formatPhoneInput(e.target.value))}
            placeholder="(555) 123-4567"
            error={!!editPhone && !isValidPhone(editPhone)}
            helperText={editPhone && !isValidPhone(editPhone) ? 'Enter a valid 10-digit phone number' : ''}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Client Tier"
            fullWidth
            value={editClientTier}
            onChange={(e) => setEditClientTier(e.target.value as ClientTier)}
            helperText={
              CLIENT_TIER_OPTIONS.find((o) => o.value === editClientTier)?.description
            }
          >
            {CLIENT_TIER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {/* MWA-357 — independent of the tier: Luke runs wild card outreach
              for "a couple of clients", executive or not. */}
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Switch
                checked={editWildcardOutreach}
                onChange={(e) => setEditWildcardOutreach(e.target.checked)}
              />
            }
            label="Wild Card Outreach"
          />
          <FormHelperText sx={{ mt: 0, ml: 0 }}>
            Adds a Wild Card Outreach tab to Front Door, for cold outreach to companies outside the
            client&rsquo;s network. Turning it off hides the tab and keeps its entries.
          </FormHelperText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveClientInfo}
            variant="contained"
            disabled={saving || !editName.trim() || !editEmail.trim()}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* North Star Publish Confirmation Dialog */}
      <Dialog
        open={northStarPublishDialogOpen}
        onClose={() => {
          setNorthStarPublishDialogOpen(false)
          setPendingPublishAttachment(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish North Star Document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This North Star document will be published to the client portal and an email will be
            sent to <strong>{client.name}</strong> notifying them that their North Star document is
            ready for review.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setNorthStarPublishDialogOpen(false)
              setPendingPublishAttachment(null)
            }}
            disabled={publishingNorthStar}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmNorthStarPublish}
            variant="contained"
            disabled={publishingNorthStar}
          >
            {publishingNorthStar ? <CircularProgress size={20} /> : 'Publish & Notify Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
